import { create } from 'zustand';
import { createStore, type StoreApi } from 'zustand/vanilla';
import { devtools } from 'zustand/middleware';
import { createEmptyValidationResult } from '@/utils/validation/validationUtils';
import { keyBy } from '@/utils/data/collections';
import { IssueSeverity } from '@/api/generated-types';
import type {
  ErrorBreakdownCargoItem,
  ErrorBreakdownEntry,
  ErrorBreakdownField,
  ErrorReport,
  ImdgSubstance,
  SubstanceComparisonData,
  SubstanceVerificationResult,
  AddOnAnswers,
  AddOnAnswersById,
} from '@/api/types';
import { deriveErrorMapsFromBreakdown } from '@/utils/validation/errorReportMapping';
import type { ValidationWarning } from '@/utils/validation/documentValidation';
import type { ValidationError } from '@/utils/errors/apiErrors';

// ── Helpers ──

function buildValidationIndex(results: ValidationResult[]) {
  return keyBy(results, (r) => r.cargoId);
}

type ErrorMapKey = 'dangerousGoodsErrors' | 'ctuFieldErrors';

function withMap(
  state: ValidationState,
  key: ErrorMapKey,
  mutate: (m: Map<string, RowErrors>) => void
) {
  const updated = new Map(state[key]);
  mutate(updated);
  return { [key]: updated };
}

// ── Types ──
export interface FieldError {
  label: string;
  message: string;
  severity: IssueSeverity;
}

export interface RowErrors {
  [field: string]: FieldError;
}

// Banner-ready breakdown types — re-exported from generated backend types
export type CtuBreakdownEntry = ErrorBreakdownEntry;
export type CtuCargoItem = ErrorBreakdownCargoItem;
export type CtuCargoField = ErrorBreakdownField;

export interface ValidationResult extends Omit<
  SubstanceVerificationResult,
  'autoSelectedSubstanceId'
> {
  autoSelectedSubstanceId?: string;
  selectedSubstance?: ImdgSubstance;
}

export interface SelectionModalState {
  isOpen: boolean;
  /** Cargo the queue opens on, pre-ticked; null opens on the first open group. */
  focusCargoId: string | null;
}

export interface ValidationState {
  validationResults: ValidationResult[];
  selectionModal: SelectionModalState;

  // Derived indexes — O(1) lookup by cargoId
  validationByCargoId: Map<string, ValidationResult>;

  // Backend-computed comparison data keyed by cargoId
  comparisonsByCargoId: Map<string, SubstanceComparisonData>;

  // Each IMDG substance's add-on answers, keyed by substance id
  addOnAnswersById: Map<string, AddOnAnswers>;

  // User overrides for fields explicitly marked as matching
  fieldMatchOverrides: Map<string, Set<string>>;

  dangerousGoodsErrors: Map<string, RowErrors>;
  ctuFieldErrors: Map<string, RowErrors>;
  errorBreakdown: CtuBreakdownEntry[];
  submissionErrors: ValidationError[];
  extractionWarnings: ValidationWarning[];
  isCheckingErrors: boolean;

  /** Frozen snapshot of error field keys per cargoId at scoped-store creation.
   *  Drives shouldShowSection in error-review mode so sections stay visible
   *  while users clear errors. Empty on the global store. */
  _initialErrorFieldsByCargoId: Map<string, Set<string>>;
}

export interface ValidationActions {
  clearResultForCargo: (cargoId: string) => void;

  setComparisons: (comparisons: Record<string, SubstanceComparisonData> | null | undefined) => void;
  /** Replaces the answers of each substance sent; an empty entry clears them. */
  setAddOnAnswers: (answers: AddOnAnswersById | null | undefined) => void;

  openSelectionModal: (focusCargoId?: string | null) => void;
  closeSelectionModal: () => void;

  batchCommit: (
    results: ValidationResult[],
    options?: {
      /** Optimistically drop existing row errors for these cargoIds before merge. */
      clearErrorsFor?: readonly string[];
    }
  ) => void;
  resetValidation: () => void;
  applyErrorReport: (
    report: ErrorReport | null | undefined,
    options?: { mergeWithExisting?: boolean }
  ) => void;

  // Cargo error actions (keyed by cargoId)
  setRowErrors: (errorsMap: Map<string, RowErrors>) => void;
  updateCargoErrors: (cargoId: string, errors: RowErrors) => void;
  clearRowErrors: (cargoId: string) => void;
  clearRowErrorFields: (cargoId: string, fields: readonly string[]) => void;
  clearRowErrorFieldsMany: (cargoIds: readonly string[], fields: readonly string[]) => void;
  setRowErrorField: (cargoId: string, field: string, error: FieldError | undefined) => void;

  // CTU error actions (keyed by ctuId) — same pattern as cargo
  setCtuFieldErrors: (errorsMap: Map<string, RowErrors>) => void;
  updateCtuErrors: (ctuId: string, errors: RowErrors) => void;
  clearCtuErrors: (ctuId: string) => void;
  clearCtuErrorFields: (ctuId: string, fields: readonly string[]) => void;
  setCtuErrorField: (ctuId: string, field: string, error: FieldError | undefined) => void;

  clearAllErrors: () => void;

  // Banner breakdown — set from backend, patched on blur
  setErrorBreakdown: (breakdown: CtuBreakdownEntry[]) => void;

  setSubmissionErrors: (errors: ValidationError[]) => void;
  clearSubmissionErrors: () => void;

  setFieldMatchOverride: (cargoId: string, fieldKey: string, matches: boolean) => void;
  clearFieldMatchOverrides: (cargoIds: readonly string[]) => void;

  setCheckingErrors: (checking: boolean) => void;

  setExtractionWarnings: (warnings: ValidationWarning[]) => void;
  resetExtractionState: () => void;

  removeCargoByIds: (cargoIds: string[]) => void;
  removeCtuErrors: (ctuId: string) => void;
}

export type ValidationStore = ValidationState & ValidationActions;

const initialModalState: SelectionModalState = { isOpen: false, focusCargoId: null };

const initialState: ValidationState = {
  validationResults: [],
  selectionModal: initialModalState,
  validationByCargoId: new Map(),
  comparisonsByCargoId: new Map(),
  addOnAnswersById: new Map(),
  fieldMatchOverrides: new Map(),
  dangerousGoodsErrors: new Map(),
  ctuFieldErrors: new Map(),
  errorBreakdown: [],
  submissionErrors: [],
  extractionWarnings: [],
  isCheckingErrors: false,
  _initialErrorFieldsByCargoId: new Map(),
};

function snapshotErrorFields(errors: Map<string, RowErrors>): Map<string, Set<string>> {
  return new Map([...errors].map(([cargoId, errs]) => [cargoId, new Set(Object.keys(errs))]));
}

// ── Action builders ──
type SetFn = (
  partial: Partial<ValidationStore> | ((state: ValidationStore) => Partial<ValidationStore>),
  replace?: false,
  name?: string
) => void;

function buildResultActions(set: SetFn) {
  return {
    clearResultForCargo: (cargoId: string) =>
      set(
        (state) => {
          const newValidationResults = state.validationResults.map((r) =>
            r.cargoId === cargoId ? createEmptyValidationResult(cargoId) : r
          );
          const newComparisons = new Map(state.comparisonsByCargoId);
          newComparisons.delete(cargoId);
          return {
            validationResults: newValidationResults,
            validationByCargoId: buildValidationIndex(newValidationResults),
            comparisonsByCargoId: newComparisons,
            ...withMap(state, 'dangerousGoodsErrors', (m) => m.delete(cargoId)),
          };
        },
        false,
        'clearResultForCargo'
      ),

    setComparisons: (comparisons: Record<string, SubstanceComparisonData> | null | undefined) =>
      set(
        (state) => {
          if (!comparisons) return {};
          const merged = new Map(state.comparisonsByCargoId);
          let changed = false;
          for (const [cargoId, data] of Object.entries(comparisons)) {
            if (data && !Object.is(merged.get(cargoId), data)) {
              merged.set(cargoId, data);
              changed = true;
            }
          }
          return changed ? { comparisonsByCargoId: merged } : {};
        },
        false,
        'setComparisons'
      ),

    setAddOnAnswers: (answers: AddOnAnswersById | null | undefined) =>
      set(
        (state) => {
          if (!answers) return {};
          const merged = new Map(state.addOnAnswersById);
          for (const [substanceId, byRegulation] of Object.entries(answers)) {
            merged.set(substanceId, byRegulation);
          }
          return { addOnAnswersById: merged };
        },
        false,
        'setAddOnAnswers'
      ),
  };
}

function buildModalActions(set: SetFn) {
  return {
    openSelectionModal: (focusCargoId: string | null = null) =>
      set({ selectionModal: { isOpen: true, focusCargoId } }, false, 'openSelectionModal'),

    closeSelectionModal: () =>
      set({ selectionModal: initialModalState }, false, 'closeSelectionModal'),
  };
}

/** Generic error map actions — same logic for cargo (by cargoId) and CTU (by ctuId). */
function buildErrorMapActions(set: SetFn, key: ErrorMapKey, prefix: string) {
  return {
    setAll: (errorsMap: Map<string, RowErrors>) =>
      set({ [key]: errorsMap }, false, `${prefix}/setAll`),

    update: (id: string, errors: RowErrors) =>
      set(
        (state) => {
          const hasErrors = Object.keys(errors).length > 0;
          const existing = state[key].get(id);

          if (!hasErrors && !existing) return {};
          if (
            existing &&
            hasErrors &&
            Object.keys(errors).length === Object.keys(existing).length &&
            Object.entries(errors).every(
              ([k, v]) => existing[k]?.message === v.message && existing[k]?.severity === v.severity
            )
          )
            return {};

          return withMap(state, key, (m) => {
            if (hasErrors) m.set(id, errors);
            else m.delete(id);
          });
        },
        false,
        `${prefix}/update`
      ),

    clear: (id: string) =>
      set((state) => withMap(state, key, (m) => m.delete(id)), false, `${prefix}/clear`),

    setField: (id: string, field: string, error: FieldError | undefined) =>
      set(
        (state) =>
          withMap(state, key, (m) => {
            const current = m.get(id) || {};
            if (error) {
              m.set(id, { ...current, [field]: error });
            } else {
              const { [field]: _, ...rest } = current;
              if (Object.keys(rest).length > 0) m.set(id, rest);
              else m.delete(id);
            }
          }),
        false,
        `${prefix}/setField`
      ),

    clearFields: (id: string, fields: readonly string[]) =>
      set(
        (state) =>
          withMap(state, key, (m) => {
            const current = m.get(id);
            if (!current) return;
            const updated = { ...current };
            for (const f of fields) delete updated[f];
            if (Object.keys(updated).length > 0) m.set(id, updated);
            else m.delete(id);
          }),
        false,
        `${prefix}/clearFields`
      ),

    clearFieldsMany: (ids: readonly string[], fields: readonly string[]) =>
      set(
        (state) =>
          withMap(state, key, (m) => {
            for (const id of ids) {
              const current = m.get(id);
              if (!current) continue;
              const updated = { ...current };
              for (const f of fields) delete updated[f];
              if (Object.keys(updated).length > 0) m.set(id, updated);
              else m.delete(id);
            }
          }),
        false,
        `${prefix}/clearFieldsMany`
      ),
  };
}

function buildErrorActions(set: SetFn) {
  const cargo = buildErrorMapActions(set, 'dangerousGoodsErrors', 'cargoErrors');
  const ctu = buildErrorMapActions(set, 'ctuFieldErrors', 'ctuErrors');

  return {
    // Cargo error actions
    setRowErrors: cargo.setAll,
    updateCargoErrors: cargo.update,
    clearRowErrors: cargo.clear,
    setRowErrorField: cargo.setField,
    clearRowErrorFields: cargo.clearFields,
    clearRowErrorFieldsMany: cargo.clearFieldsMany,

    // CTU error actions
    setCtuFieldErrors: ctu.setAll,
    updateCtuErrors: ctu.update,
    clearCtuErrors: ctu.clear,
    clearCtuErrorFields: ctu.clearFields,
    setCtuErrorField: ctu.setField,

    clearAllErrors: () =>
      set(
        { dangerousGoodsErrors: new Map(), ctuFieldErrors: new Map(), errorBreakdown: [] },
        false,
        'clearAllErrors'
      ),

    setErrorBreakdown: (breakdown: CtuBreakdownEntry[]) =>
      set({ errorBreakdown: breakdown }, false, 'setErrorBreakdown'),

    setSubmissionErrors: (errors: ValidationError[]) =>
      set({ submissionErrors: errors }, false, 'setSubmissionErrors'),

    clearSubmissionErrors: () => set({ submissionErrors: [] }, false, 'clearSubmissionErrors'),
  };
}

function buildWarningActions(set: SetFn) {
  return {
    setFieldMatchOverride: (cargoId: string, fieldKey: string, matches: boolean) =>
      set(
        (state) => {
          const overrides = new Map(state.fieldMatchOverrides);
          const cargoOverrides = new Set(overrides.get(cargoId) ?? []);
          if (matches) cargoOverrides.add(fieldKey);
          else cargoOverrides.delete(fieldKey);
          if (cargoOverrides.size > 0) overrides.set(cargoId, cargoOverrides);
          else overrides.delete(cargoId);
          return { fieldMatchOverrides: overrides };
        },
        false,
        'setFieldMatchOverride'
      ),

    clearFieldMatchOverrides: (cargoIds: readonly string[]) =>
      set(
        (state) => {
          if (!cargoIds.some((id) => state.fieldMatchOverrides.has(id))) return {};
          const overrides = new Map(state.fieldMatchOverrides);
          for (const id of cargoIds) overrides.delete(id);
          return { fieldMatchOverrides: overrides };
        },
        false,
        'clearFieldMatchOverrides'
      ),

    setCheckingErrors: (checking: boolean) =>
      set(
        (state) => (state.isCheckingErrors === checking ? {} : { isCheckingErrors: checking }),
        false,
        'setCheckingErrors'
      ),

    setExtractionWarnings: (warnings: ValidationWarning[]) =>
      set({ extractionWarnings: warnings }, false, 'setExtractionWarnings'),

    resetExtractionState: () =>
      set(
        { extractionWarnings: [], ctuFieldErrors: new Map(), errorBreakdown: [] },
        false,
        'resetExtractionState'
      ),
  };
}

function mergeErrorMap(
  incoming: Map<string, RowErrors>,
  existing: Map<string, RowErrors> | undefined,
  merge: boolean
): Map<string, RowErrors> {
  if (merge && incoming.size > 0) {
    const merged = new Map(existing);
    for (const [id, errors] of incoming) {
      merged.set(id, { ...(merged.get(id) ?? {}), ...errors });
    }
    return merged;
  }
  return merge ? (existing ?? new Map()) : incoming;
}

function buildLifecycleActions(set: SetFn) {
  return {
    batchCommit: (results: ValidationResult[], options?: { clearErrorsFor?: readonly string[] }) =>
      set(
        (state) => {
          const toDelete = options?.clearErrorsFor?.filter((id) =>
            state.dangerousGoodsErrors.has(id)
          );
          const patch: Partial<ValidationStore> = {
            validationResults: results,
            validationByCargoId: buildValidationIndex(results),
          };
          if (toDelete?.length) {
            const nextErrors = new Map(state.dangerousGoodsErrors);
            for (const id of toDelete) nextErrors.delete(id);
            patch.dangerousGoodsErrors = nextErrors;
          }
          return patch;
        },
        false,
        'batchCommit'
      ),

    resetValidation: () => set({ ...initialState }, false, 'resetValidation'),

    applyErrorReport: (
      report: ErrorReport | null | undefined,
      options?: { mergeWithExisting?: boolean }
    ) => {
      if (!report) return;
      const merge = !!options?.mergeWithExisting;
      const { cargoErrors, ctuErrors } = deriveErrorMapsFromBreakdown(report.breakdown);
      set(
        (state) => ({
          dangerousGoodsErrors: mergeErrorMap(
            cargoErrors,
            merge ? state.dangerousGoodsErrors : undefined,
            merge
          ),
          ctuFieldErrors: mergeErrorMap(ctuErrors, merge ? state.ctuFieldErrors : undefined, merge),
          errorBreakdown: report.breakdown ?? [],
        }),
        false,
        'applyErrorReport'
      );
    },
  };
}

function buildCargoCleanupActions(set: SetFn) {
  const removeCargoByIds: ValidationActions['removeCargoByIds'] = (cargoIds) =>
    set(
      (state) => {
        const ids = new Set(cargoIds);
        const newValidationResults = state.validationResults.filter(
          (r) => !r.cargoId || !ids.has(r.cargoId)
        );
        const newErrors = new Map<string, RowErrors>();
        state.dangerousGoodsErrors.forEach((errors, key) => {
          if (!ids.has(key)) newErrors.set(key, errors);
        });
        const newComparisons = new Map(state.comparisonsByCargoId);
        for (const id of ids) newComparisons.delete(id);
        const newOverrides = new Map(state.fieldMatchOverrides);
        for (const id of ids) newOverrides.delete(id);
        const newBreakdown = state.errorBreakdown
          .map((entry) => ({
            ...entry,
            items: entry.items?.filter((item) => !item.cargoId || !ids.has(item.cargoId)),
          }))
          .filter((entry) => (entry.items?.length ?? 0) > 0 || (entry.ctuFields?.length ?? 0) > 0);
        return {
          validationResults: newValidationResults,
          validationByCargoId: buildValidationIndex(newValidationResults),
          comparisonsByCargoId: newComparisons,
          fieldMatchOverrides: newOverrides,
          dangerousGoodsErrors: newErrors,
          errorBreakdown: newBreakdown,
        };
      },
      false,
      'removeCargoByIds'
    );

  const removeCtuErrors = (ctuId: string) =>
    set(
      (state) => {
        const newCtuErrors = new Map(state.ctuFieldErrors);
        newCtuErrors.delete(ctuId);
        const newBreakdown = state.errorBreakdown.filter((entry) => entry.ctuId !== ctuId);
        return { ctuFieldErrors: newCtuErrors, errorBreakdown: newBreakdown };
      },
      false,
      'removeCtuErrors'
    );

  return {
    removeCargoByIds,
    removeCtuErrors,
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- Zustand's internal set signature uses overloads incompatible with stricter types
function buildActions(rawSet: (...args: any[]) => any): ValidationActions {
  const set = rawSet as SetFn;
  return {
    ...buildResultActions(set),
    ...buildModalActions(set),
    ...buildErrorActions(set),
    ...buildWarningActions(set),
    ...buildLifecycleActions(set),
    ...buildCargoCleanupActions(set),
  };
}

// ── Factory: creates a vanilla store instance (for dialog) ──
export function createValidationStore(
  initial?: Partial<ValidationState>
): StoreApi<ValidationStore> {
  const seedErrors = initial?.dangerousGoodsErrors ?? new Map();
  return createStore<ValidationStore>()(
    devtools(
      (set) => ({
        ...initialState,
        ...initial,
        validationByCargoId: buildValidationIndex(initial?.validationResults ?? []),
        comparisonsByCargoId: initial?.comparisonsByCargoId ?? new Map(),
        dangerousGoodsErrors: seedErrors,
        ctuFieldErrors: initial?.ctuFieldErrors ?? new Map(),
        _initialErrorFieldsByCargoId: snapshotErrorFields(seedErrors),
        ...buildActions(set),
      }),
      { name: 'ValidationStore (scoped)', enabled: import.meta.env.DEV }
    )
  );
}

// ── Global singleton (main workflow) ──
// Not persisted: the loader runs resetValidation() on every transaction open before any
// stored state is read, so persisted state was never consumed — yet persisting cost a
// ~3,000-row map + JSON.stringify + sync localStorage write on every set().
export const globalValidationStore = create<ValidationStore>()(
  devtools(
    (set) => ({
      ...initialState,
      ...buildActions(set),
    }),
    { name: 'ValidationStore', enabled: import.meta.env.DEV }
  )
);

// One-time cleanup of the removed persist key so old multi-MB blobs don't linger.
try {
  localStorage.removeItem('validation-storage');
} catch {
  // localStorage unavailable (private mode / non-browser) — nothing to clean up.
}

export default globalValidationStore;
